import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbs, useUser } from '../hooks';
import { Button, Field, Groupper, Header, Icon, Input, Message, Modal, Pagination, Table, Toast } from 'react-frontier';
import { bindClick, bindFormChange } from '../SiteUtil';
import { ExpenseRejectReason, SetLoading } from '../SiteClasses';
import UserAccess from '../UserAccess';
import API from '../API';
import { Dropdown } from 'semantic-ui-react';

const PAGE_SIZE = 20;
interface RejectReasonForm {
	reason: string;
}

var ExpenseRejectReasonsList = ()=>{
	var { hasAccess } = useUser();
	var [reasons, setReasons] = useState<ExpenseRejectReason[]>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [filter, setFilter] = useState<string>('');
	var [searchReasons, setSearchReasons] = useState<ExpenseRejectReason[]>(null);
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [searching, setSearching] = useState<boolean>(false);
	var [page, setPage] = useState<number>(0);
	var [loadError, setLoadError] = useState<string>(null);
	
	var [showCreateModal, setShowCreateModal] = useState<boolean>(false);
	var [showEditModal, setShowEditModal] = useState<boolean>(false);
	var [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	var [selectedReason, setSelectedReason] = useState<ExpenseRejectReason | null>(null);
	var [form, setForm] = useState<RejectReasonForm>({ reason: '' });
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		get();
	}, []);

	var get = (get_page?: number)=>{
		var offset = (get_page > 0) ? Math.max(0, (get_page || page) * PAGE_SIZE) : 0;
		setLoading(true);
		API.getExpenseRejectReasons().then(res=>{
			if(res.error) return setLoadError(res.message);
			return setReasons(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error al obtener las razones de rechazo. (GET-1)');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onSearchChange = (value: string)=>{
		setFilter(value);
		if(searchTimeout) clearTimeout(searchTimeout);
		
		if(!value.trim()){
			setSearchReasons(null);
			setSearching(false);
			return;
		}

		setSearching(true);
		var timeout = setTimeout(()=>{
			var filtered = reasons?.filter(r=>
				r.reason.toLowerCase().includes(value.toLowerCase())
			) || [];
			setSearchReasons(filtered);
			setSearching(false);
		}, 300);
		setSearchTimeout(timeout);
	};

	var onPageChange = (newPage: number)=>{
		setPage(newPage);
		get(newPage);
	};

	var onCreate = ()=>{
		setForm({ reason: '' });
		setModalPrompts(null);
		setShowCreateModal(true);
	};

	var onEdit = (reason: ExpenseRejectReason)=>{
		setSelectedReason(reason);
		setForm({
			reason: reason.reason
		});
		setModalPrompts(null);
		setShowEditModal(true);
	};

	var onDelete = (reason: ExpenseRejectReason)=>{
		setSelectedReason(reason);
		setModalPrompts(null);
		setShowDeleteModal(true);
	};

	var create = async (setLoading: SetLoading)=>{
		if(!form.reason.trim()){
			setModalPrompts(['La razón de rechazo es requerida']);
			return;
		}

		setLoading(true);
		API.createExpenseRejectReason(form.reason.trim()).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Razón de rechazo creada exitosamente');
			setReasons(prev=>[...prev, { reason_id: res.data.reason_id, reason: form.reason.trim() }]);
			setShowCreateModal(false);
		}).catch(err=>{
			setModalPrompts(['Error inesperado creando la razón de rechazo']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var edit = async (setLoading: SetLoading)=>{
		if(!form.reason.trim()){
			setModalPrompts(['La razón de rechazo es requerida']);
			return;
		}

		setLoading(true);
		API.editExpenseRejectReason(selectedReason.reason_id, form.reason.trim()).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Razón de rechazo actualizada exitosamente');
			setReasons(prev=>prev.map(r=>r.reason_id === selectedReason.reason_id ? { ...r, reason: form.reason.trim() } : r));
			setShowEditModal(false);
		}).catch(err=>{
			setModalPrompts(['Error inesperado actualizando la razón de rechazo']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var submitDelete = async (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteExpenseRejectReason(selectedReason.reason_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Razón de rechazo eliminada exitosamente');
			setReasons(prev=>prev.filter(r=>r.reason_id !== selectedReason.reason_id));
			setShowDeleteModal(false);
		}).catch(err=>{
			setModalPrompts(['Error inesperado eliminando la razón de rechazo']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onFormChange = bindFormChange(form, setForm);

	if(loadError) return <Header text='Error' subtext={loadError} />
	if(!reasons) return <Header text='Cargando datos' loading/>

	var shown_reasons = searchReasons || reasons;

	return (
		<div>
			<Groupper
				title={'Razones de rechazo'}
				titleRight={hasAccess(UserAccess.ACCOUNTING.CREATE_REJECT_REASON) && (
					<Button iconName='plus' color='black' text='Agregar' size='small' onClick={onCreate}/>
				)}
				style={{ marginTop: 20 }}
				width={600}
			>
				<Input placeholder='Buscar razón de rechazo...' value={filter} onChange={onSearchChange} />
				<Table striped fitted
					className='last top'
					style={{ marginTop: 15 }}
					collapsingIndexes={[0, 2]}
					headers={['ID', 'Razón', <Icon name='wrench' />]}
					centeredIndexes={[2]}
					footer={(!!shown_reasons && shown_reasons.length >= PAGE_SIZE) && (
						<Table.Cell colSpan={3} row>
							<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
								<Pagination page={page} onPageChange={onPageChange} hasNext={shown_reasons.length>=PAGE_SIZE} />
							</div>
						</Table.Cell>
					)}
				>
					{searching || loading ? (
						<Table.Cell row className='empty' colSpan={3}>
							<Header loading text='Cargando datos' />
						</Table.Cell>
					) : (
						shown_reasons.length !== 0 ? (
							shown_reasons.map(r=>(
								<Table.Row
									key={r.reason_id}
									collapsingIndexes={[0, 2]}
									data={[
										r.reason_id,
										r.reason,
										<Dropdown key={r.reason_id} icon={(
											<div className="fr label" style={{ padding: '3px 10px' }}>
												<Icon name='ellipsis' style={{ margin: 0 }} />
											</div>
										)} className='icon'>
											<Dropdown.Menu>
												{hasAccess(UserAccess.ACCOUNTING.EDIT_REJECT_REASON) && (
													<Dropdown.Item onClick={()=>onEdit(r)}>
														<Icon name="pen" style={{ marginRight: 3 }} /> Editar
													</Dropdown.Item>
												)}
												{hasAccess(UserAccess.ACCOUNTING.DELETE_REJECT_REASON) && (
													<Dropdown.Item onClick={()=>onDelete(r)}>
														<Icon name="trash" style={{ marginRight: 3 }} /> Eliminar
													</Dropdown.Item>
												)}
											</Dropdown.Menu>
										</Dropdown>
									]}
								/>
							))
						) : (
							<Table.Cell row className='empty' colSpan={3}>
								No se encontraron registros
							</Table.Cell>
						)
					)}
				</Table>
			</Groupper>
			<Modal open={showCreateModal} onClose={bindClick(setShowCreateModal, false)} size="tiny">
				<Modal.Header>Agregar razón</Modal.Header>
				<Modal.Content>
					<Field label="Nombre de la razón">
						<Input
							value={form.reason}
							onChange={onFormChange('reason')}
							placeholder="Ej: Documentación incompleta, Monto excedido, etc."
						/>
					</Field>
					<Message type="error" list={modalPrompts} />
				</Modal.Content>
				<Modal.Actions>
					<Button text="Cancelar" basic onClick={bindClick(setShowCreateModal, false)} />
					<Button text="Crear" color="black" onClick={create} />
				</Modal.Actions>
			</Modal>
			<Modal open={showEditModal} onClose={bindClick(setShowEditModal, false)} size="tiny">
				<Modal.Header>Editar Razón de Rechazo</Modal.Header>
				<Modal.Content>
					<Input
						label="Razón de Rechazo"
						value={form.reason}
						onChange={onFormChange('reason')}
						placeholder="Ej: Documentación incompleta, Monto excedido, etc."
					/>
					<Message type="error" list={modalPrompts} />
				</Modal.Content>
				<Modal.Actions>
					<Button text="Cancelar" basic onClick={bindClick(setShowEditModal, false)} />
					<Button text="Guardar" color="black" onClick={edit} />
				</Modal.Actions>
			</Modal>
			<Modal open={showDeleteModal} onClose={bindClick(setShowDeleteModal, false)} size="tiny">
				<Modal.Header>Eliminar Razón de Rechazo</Modal.Header>
				<Modal.Content>
					{selectedReason && (
						<div>
							<Header
								text="¿Eliminar razón de rechazo?"
								subtext={`Se eliminará permanentemente la razón "${selectedReason.reason}"`}
								size="small"
							/>
							<Message type="warning" list={['Esta acción no se puede deshacer']} />
							<Message type="error" list={modalPrompts} />
						</div>
					)}
				</Modal.Content>
				<Modal.Actions>
					<Button text="Cancelar" basic onClick={bindClick(setShowDeleteModal, false)} />
					<Button text="Eliminar" color="red" onClick={submitDelete} />
				</Modal.Actions>
			</Modal>
		</div>
	);
};

export default ExpenseRejectReasonsList;
